import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";

/**
 * i18n bootstrap per ADR-i18n-react-i18next-en-first.
 *
 * Defaults:
 *  - fallbackLng: 'en'   — EN bundles inline (always first paint)
 *  - supportedLngs: ['en', 'tr']
 *  - detection.order: localStorage > navigator > htmlTag
 *  - detection.caches: ['localStorage']
 *
 * Lazy-loaded locales: TR is dynamically imported only after the user actually
 * picks Turkish (or it's their browser default), keeping the initial bundle
 * lean. Vite splits each lazy import into its own chunk; tr.json + i18next
 * runtime overhead arrives on demand instead of on every first-paint.
 */
export const SUPPORTED_LANGS = ["en", "tr"] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

const LAZY_LOADERS: Record<Exclude<SupportedLang, "en">, () => Promise<{ default: any }>> = {
  // Vite dynamic-import → produces a separate chunk in dist/assets/.
  tr: () => import("./locales/tr.json"),
};

const _loaded = new Set<SupportedLang>(["en"]);

async function ensureLoaded(lng: string): Promise<void> {
  const code = (lng || "en").slice(0, 2) as SupportedLang;
  if (_loaded.has(code)) return;
  const loader = (LAZY_LOADERS as any)[code];
  if (!loader) return;
  try {
    const mod = await loader();
    i18n.addResourceBundle(code, "translation", mod.default || mod, true, true);
    _loaded.add(code);
    // Force every component using useTranslation() to re-render after the bundle
    // lands. Without this, the initial render uses EN fallback and never
    // refreshes (no `languageChanged` event fires automatically on bundle add).
    if (i18n.language?.toLowerCase().startsWith(code)) {
      i18n.emit("languageChanged", i18n.language);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[i18n] Failed to load locale ${code}:`, err);
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
    },
    fallbackLng: "en",
    supportedLngs: ["en", "tr"],
    // Normalize browser locales: "tr-TR" → "tr", "en-US" → "en". Without this
    // i18next rejects "tr-TR" as unsupported and falls back to EN even when
    // tr.json is loaded.
    load: "languageOnly",
    nonExplicitSupportedLngs: true,
    interpolation: {
      escapeValue: false,
    },
    // EN-first per ADR-i18n-react-i18next-en-first:
    //   - On first visit (no localStorage): use fallbackLng "en" → instant EN paint
    //   - User explicitly switches via header LanguageSwitcher → cached to localStorage
    //   - Next visit reads cached value → consistent
    // Navigator detection intentionally REMOVED — it creates "half EN, half TR"
    // races where a "tr-TR" browser triggers async TR load while sidebar
    // already rendered with EN fallback.
    detection: {
      order: ["localStorage"],
      lookupLocalStorage: "growtify-cockpit-lng",
      caches: ["localStorage"],
    },
    returnEmptyString: false,
    // Disable suspense — we load locales on-demand and fall back to EN until the chunk lands
    react: { useSuspense: false },
  });

// Eagerly fetch the resolved language's bundle if it isn't EN.
ensureLoaded(i18n.resolvedLanguage || i18n.language || "en");

// Wire ensureLoaded into language switches so changeLanguage('tr') triggers the
// chunk fetch automatically. i18next swaps the active language only after the
// resourceBundle is registered — so we await before the change actually applies.
const _origChangeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = (async (lng?: string, ...rest: any[]) => {
  if (lng) await ensureLoaded(lng);
  return _origChangeLanguage(lng as any, ...rest);
}) as typeof i18n.changeLanguage;

export default i18n;
